import { isPlatformBrowser } from '@angular/common';
import type { OnDestroy, OnInit } from '@angular/core';
import {
  ChangeDetectionStrategy,
  ChangeDetectorRef,
  Component,
  EventEmitter,
  Inject,
  Input,
  Output,
  PLATFORM_ID,
} from '@angular/core';
import { NonNullableFormBuilder } from '@angular/forms';
import { ActivatedRoute } from '@angular/router';
import type {
  AppleSignInResponse,
  SSOResponseData,
  SSOUser,
} from '@freelancer/auth';
import { AUTH_CONFIG, AppleSSO, AuthConfig } from '@freelancer/auth';
import type { BackendAllErrorCodes } from '@freelancer/datastore';
import { Datastore, OrderByDirection } from '@freelancer/datastore';
import type {
  CountriesCollection,
  Country,
  UserRequiresGdprCollection,
} from '@freelancer/datastore/collections';
import { CountryCode, countries } from '@freelancer/datastore/collections';
import type { FacebookAuthResponse } from '@freelancer/facebook';
import { Facebook } from '@freelancer/facebook';
import { FeatureFlagsService } from '@freelancer/feature-flags';
import type { ResponseData } from '@freelancer/freelancer-http';
import type { GoogleAuthResponse } from '@freelancer/google-sign-in';
import { GoogleSignInService } from '@freelancer/google-sign-in';
import type {
  AuthResponse,
  AuthSuccessResponseAjax,
  LoginError,
  UserCheckError,
} from '@freelancer/login-signup';
import { LoginSignupService } from '@freelancer/login-signup';
import { ThreatmetrixService } from '@freelancer/threatmetrix';
import { FacebookStandardEvents, Tracking } from '@freelancer/tracking';
import type { EmptyObject } from '@freelancer/types';
import type { SelectItem } from '@freelancer/ui/select';
import {
  containsURLRegex,
  maxLength,
  minLength,
  notEqualOtherControl,
  notPattern,
  pattern,
  required,
  requiredTruthy,
  validEmailRegex,
  validPasswordRegex,
  validUsernameRegex,
} from '@freelancer/ui/validators';
import { EmptyValue } from '@freelancer/utils';
import { UntilDestroy, untilDestroyed } from '@ngneat/until-destroy';
import { RoleApi } from 'api-typings/common/common';
import { ErrorCodeApi } from 'api-typings/errors/errors';
import { CookieService } from 'ngx-cookie';
import type { Observable } from 'rxjs';
import { Subscription, firstValueFrom } from 'rxjs';
import { map, take } from 'rxjs/operators';
import type { LoginSignupUserStatus } from '../login-signup.types';
import { SuccessStatus } from '../login-signup.types';
import { SuccessAction } from '../success-redirect';
import type { SignupDetailsFormGroup } from './signup.types';

enum SignupStep {
  DETAILS = 0,
  USERNAME_SELECT,
  ACCOUNT_TYPE,
  GDPR,
  FACEBOOK,
  RECOVERY_EMAIL,
  CAPTCHA,
  SUCCESS,
  LOGIN_SUCCESS,
}

@Component({
  standalone: false,
  selector: 'app-sign-up-header',
  template: ` <ng-content></ng-content> `,
  changeDetection: ChangeDetectionStrategy.OnPush,
})
export class SignUpHeaderComponent {}

@UntilDestroy({ className: 'SignupComponent' })
@Component({
  standalone: false,
  selector: 'app-signup',
  template: `
    <ng-container [ngSwitch]="currentStep">
      <app-details-form
        i18n="Create your Freelancer account"
        *ngSwitchCase="SignupStep.DETAILS"
        [defaultHeading]="defaultHeading"
        [showSSO]="showSSO"
        [showAppleSignin]="
          showAppleSigninPromise && (showAppleSigninPromise | flAsync) === true
        "
        [partnerDomain]="partnerDomain"
        [showGoogleButton]="
          showGoogleLogin &&
          isGoogleEnabledPromise &&
          (isGoogleEnabledPromise | flAsync) == true
        "
        [facebookLoginStatusLoaded]="
          facebookLoginStatusPromise &&
          (facebookLoginStatusPromise | flAsync) === true
        "
        [formGroup]="detailsFormGroup"
        [response]="
          (checkDetailsPromise && (checkDetailsPromise | flAsync)) ||
          (ssoLoginPromise && (ssoLoginPromise | flAsync))
        "
        [enableLoginSignupSwitch]="enableLoginSignupSwitch"
        [showLogo]="showLogo"
        [showFields$]="showFields$"
        [countriesList$]="countriesList$"
        (complete)="handleSubmitDetails('normal')"
        (appleClick)="handleSSOSignupClick('apple')"
        (facebookClick)="handleSSOSignupClick('facebook')"
        (googleClick)="handleSSOSignupClick('google')"
        (switchToLogin)="handleSwitchToLogin()"
      >
        <app-details-form-header>
          <ng-content select="app-sign-up-header"></ng-content>
        </app-details-form-header>
      </app-details-form>
      <app-username-select-form
        *ngSwitchCase="SignupStep.USERNAME_SELECT"
        [email]="detailsFormGroup.controls.email.value"
        [baseName]="detailsFormGroup.controls.firstName.value"
        [lastName]="detailsFormGroup.controls.lastName.value"
        [formGroup]="usernameFormGroup"
        [response]="signupPromise && (signupPromise | flAsync)"
        [busy]="signupPromise && (signupPromise | flAsync) === undefined"
        (back)="handleBackAction()"
        (complete)="handleSubmitUsername()"
        (switchToLogin)="handleSwitchToLogin()"
      ></app-username-select-form>
      <app-account-type-form
        *ngSwitchCase="SignupStep.ACCOUNT_TYPE"
        [employerLabel]="employerLabel"
        [formGroup]="accountTypeFormGroup"
        [freelancerLabel]="freelancerLabel"
        [response]="signupPromise && (signupPromise | flAsync)"
        [subheaderText]="accountTypeSubheaderText"
        [busy]="signupPromise && (signupPromise | flAsync) === undefined"
        (back)="handleBackAction()"
        (complete)="handleSubmitAccountType()"
        (switchToLogin)="handleSwitchToLogin()"
      ></app-account-type-form>
      <app-signup-gdpr-form
        *ngSwitchCase="SignupStep.GDPR"
        [formGroup]="gdprFormGroup"
        [response]="signupPromise && (signupPromise | flAsync)"
        [busy]="signupPromise && (signupPromise | flAsync) === undefined"
        (back)="handleBackAction()"
        (complete)="handleSignup()"
        (switchToLogin)="handleSwitchToLogin()"
      ></app-signup-gdpr-form>
      <app-recovery-email-form
        *ngSwitchCase="SignupStep.RECOVERY_EMAIL"
        [formGroup]="detailsFormGroup"
        [user]="ssoDetailsPromise | flAsync"
        [response]="checkDetailsPromise && (checkDetailsPromise | flAsync)"
        [ssoAuth]="ssoAuthPromise | flAsync"
        (complete)="handleSubmitDetails('sso')"
        (switchToLogin)="handleSwitchToLogin()"
        (back)="handleBackToStart()"
      ></app-recovery-email-form>
      <app-captcha-form
        *ngSwitchCase="SignupStep.CAPTCHA"
        [formGroup]="captchaFormGroup"
        [partnerDomain]="partnerDomain"
        [response]="signupPromise && (signupPromise | flAsync)"
        [busy]="signupPromise && (signupPromise | flAsync) === undefined"
        (back)="handleBackToStart()"
        (complete)="handleSignup()"
        (switchToLogin)="handleSwitchToLogin()"
      ></app-captcha-form>
      <app-success-redirect
        *ngSwitchCase="SignupStep.SUCCESS"
        [action]="SuccessAction.SIGNUP"
        [domain]="partnerDomain"
      ></app-success-redirect>
      <!-- used for successful sso login on the /signup route -->
      <app-success-redirect
        *ngSwitchCase="SignupStep.LOGIN_SUCCESS"
        [action]="SuccessAction.LOGIN"
      ></app-success-redirect>
    </ng-container>
  `,
  styleUrls: ['./signup.component.scss'],
  changeDetection: ChangeDetectionStrategy.OnPush,
})
export class SignupComponent implements OnInit, OnDestroy {
  SuccessAction = SuccessAction;
  SignupStep = SignupStep;

  showAppleSigninPromise?: Promise<boolean | undefined>;

  currentStep = SignupStep.DETAILS;
  firstNameControl = this.fb.control('', [
    notPattern(containsURLRegex, $localize`Please provide a valid first name.`),
  ]);
  lastNameControl = this.fb.control('', [
    notPattern(containsURLRegex, $localize`Please provide a valid last name.`),
  ]);
  phoneCountryControl = this.fb.control(countries[CountryCode.AU]);
  phoneNumberControl = this.fb.control('');
  companyNameControl = this.fb.control('');
  emailControl = this.fb.control('', [
    required($localize`Please enter an email address.`),
    pattern(validEmailRegex, $localize`Please enter a valid email address.`),
  ]);
  detailsFormGroup: SignupDetailsFormGroup = this.fb.group(
    {
      firstName: this.firstNameControl,
      lastName: this.lastNameControl,
      phoneCountry: this.phoneCountryControl,
      phoneNumber: this.phoneNumberControl,
      companyName: this.companyNameControl,
      email: this.emailControl,
      password: [
        '',
        [
          required($localize`Please enter a password.`),
          minLength(6, $localize`Must be at least 6 characters`),
          maxLength(150, $localize`Must be at most 150 characters`),
          pattern(
            validPasswordRegex,
            $localize`Must contain lower and uppercase or a number. `,
          ),
          notEqualOtherControl(
            this.emailControl,
            $localize`Password cannot be your email.`,
          ),
        ],
      ],
      termsOfUse: [
        false,
        [
          requiredTruthy(''), // Do not put an error message as it will not appear at the bottom of the <fl-label>
        ],
      ],
    },
    {
      updateOn: 'submit',
    },
  );
  usernameFormGroup = this.fb.group(
    {
      username: [
        '',
        [
          required($localize`Please provide a username`),
          pattern(
            validUsernameRegex,
            $localize`Username must be between 3 and 16 characters long and composed of only numbers and letters, starting with a letter.`,
          ),
        ],
      ],
    },
    {
      updateOn: 'submit',
    },
  );
  accountTypeFormGroup = this.fb.group({
    accountType: this.fb.control(EmptyValue<RoleApi>(), [
      required($localize`Please select an account type`),
    ]),
  });
  gdprFormGroup = this.fb.group({
    privacyConsent: [false],
    personalUse: [false],
  });
  captchaFormGroup = this.fb.group({
    response: ['', required($localize`Please fill out the CAPTCHA form`)],
    challenge: [''],
  });

  checkDetailsPromise?: Promise<
    ResponseData<unknown, UserCheckError> | undefined
  >;
  ssoLoginPromise?: Promise<ResponseData<AuthResponse, LoginError> | undefined>;
  signupPromise?: Promise<
    | ResponseData<AuthSuccessResponseAjax, BackendAllErrorCodes | LoginError>
    | undefined
  >;

  /**
   * SSO promise via the appropriate SDK.
   * Returns the sso details for the provider
   */
  ssoAuthPromise?: Promise<
    | SSOResponseData<'facebook', FacebookAuthResponse>
    | SSOResponseData<'apple', AppleSignInResponse>
    | SSOResponseData<'google', GoogleAuthResponse>
    | undefined
  >;
  ssoDetailsPromise?: Promise<SSOUser | undefined>;
  facebookLoginStatusPromise?: Promise<boolean | undefined>;
  isGoogleEnabledPromise?: Promise<boolean | undefined>;
  countriesList$: Observable<readonly SelectItem<Country>[]>;
  showFields$: Observable<
    | {
        readonly fullname: boolean;
        readonly company: boolean;
        readonly phone: boolean;
      }
    | false
  >;

  showGoogleLogin = true;
  defaultHeading = $localize`Sign up`;

  private subscriptions = new Subscription();

  @Input() accountTypeSubheaderText?: string;
  @Input() emailAddress?: string;
  @Input() emailVerifyGoto?: string;
  @Input() employerLabel?: string;
  @Input() enableLoginSignupSwitch = true;
  @Input() forceAccountType?: RoleApi;
  @Input() freelancerLabel?: string;
  @Input() header?: string;
  @Input() isModal = false;
  @Input() partnerDomain = 'Freelancer';
  @Input() showLogo = true;
  @Input() showSSO = true;
  @Input() skipSuccessRedirectView? = false;
  @Input() shouldRedirect? = false;

  @Output() success = new EventEmitter<LoginSignupUserStatus>();
  @Output() switchToLogin = new EventEmitter();

  constructor(
    @Inject(AUTH_CONFIG) private authConfig: AuthConfig,
    @Inject(PLATFORM_ID) private platformId: object,
    private apple: AppleSSO,
    private changeDetectorRef: ChangeDetectorRef,
    private cookies: CookieService,
    private datastore: Datastore,
    private facebook: Facebook,
    private fb: NonNullableFormBuilder,
    private featureFlagsService: FeatureFlagsService,
    private googleSignInService: GoogleSignInService,
    private loginSignup: LoginSignupService,
    private route: ActivatedRoute,
    private threatmetrix: ThreatmetrixService,
    private tracking: Tracking,
  ) {}

  ngOnInit(): void {
    if (this.header) {
      this.defaultHeading = this.header;
    }

    this.showAppleSigninPromise = this.apple.isEnabled();
    this.facebookLoginStatusPromise = this.facebook.loadLoginStatus();
    this.isGoogleEnabledPromise = this.googleSignInService.isEnabled();
    const savedState = this.loginSignup.getSavedState();
    if ('user' in savedState) {
      this.detailsFormGroup.controls.email.setValue(savedState.user);
      this.detailsFormGroup.controls.password.setValue(savedState.password);
    } else {
      this.ssoAuthPromise = savedState.ssoAuthPromise;
      this.ssoDetailsPromise = savedState.ssoDetailsPromise;
      this.ssoLoginPromise = savedState.ssoActionPromise;
      this.setStep(SignupStep.RECOVERY_EMAIL);
    }

    // Use URL Parameter if Email Address is set in the URL Parameter
    this.detailsFormGroup.controls.email.setValue(
      this.route.snapshot.queryParams.email_address,
    );

    if (this.emailAddress) {
      this.detailsFormGroup.controls.email.setValue(this.emailAddress);
    }

    // force password control to revalidate when email is updated
    const emailControl = this.detailsFormGroup.controls.email;
    const passwordControl = this.detailsFormGroup.controls.password;
    this.subscriptions.add(
      emailControl.valueChanges.subscribe(_ => {
        if (passwordControl.touched && passwordControl.dirty) {
          passwordControl.updateValueAndValidity();
        }
      }),
    );

    // This is a temporary solution to set the GDPR cookie.
    // We will move this to the logged out shell at
    // https://phabricator.tools.flnltd.com/T141066
    if (isPlatformBrowser(this.platformId) && this.authConfig.gdprCookie) {
      this.subscriptions.add(
        this.datastore
          .document<UserRequiresGdprCollection>('userRequiresGdpr')
          .valueChanges()
          .pipe(
            map(({ userRequiresGdpr }) => userRequiresGdpr),
            take(1),
          )
          .subscribe(userRequiresGdpr => {
            if (this.authConfig.gdprCookie) {
              this.cookies.put(
                this.authConfig.gdprCookie,
                userRequiresGdpr.toString(),
              );
            }
          }),
      );
    }

    this.showFields$ = this.featureFlagsService.getConfig('signupModalFields');
    this.subscriptions.add(
      this.showFields$.pipe(take(1)).subscribe(showFields => {
        if (!showFields) {
          return;
        }

        if (showFields.fullname) {
          // We're using short copies for the validation errors to ensure they'll fit in one line.
          this.firstNameControl.addValidators(
            required($localize`:First name required error message: Required`),
          );
          this.lastNameControl.addValidators(
            required($localize`:Last name required error message: Required`),
          );
        }

        if (showFields.phone) {
          const allCountries$ = this.datastore
            .collection<CountriesCollection>('countries', query =>
              query.orderBy('name', OrderByDirection.ASC),
            )
            .valueChanges();

          this.countriesList$ = allCountries$.pipe(
            map(countriesList => {
              return countriesList.map(country => ({
                displayText: $localize`${country.name} +${country.phoneCode}`,
                value: country,
              }));
            }),
          );

          // Since we are only asking for phonecode in Loadshift currently, we will default to Australia
          const defaultCountry$ = this.countriesList$.pipe(
            map(countriesList => {
              return countriesList.find(selectItem => {
                const country = selectItem.value;
                return country.name === countries[CountryCode.AU].name;
              })?.value;
            }),
          );

          firstValueFrom(defaultCountry$.pipe(untilDestroyed(this))).then(
            defaultCountry => {
              if (defaultCountry) {
                this.phoneCountryControl.setValue(defaultCountry);
              }
            },
          );

          // The dropdown select needs to update to for the flag icon to update
          this.phoneCountryControl = Object.defineProperty(
            this.phoneCountryControl,
            'updateOn',
            {
              get: () => 'change',
              configurable: true,
            },
          );

          this.phoneCountryControl.setValidators(
            required(
              $localize`:Country required error message: Please select a country`,
            ),
          );
          this.phoneNumberControl.setValidators([
            pattern(
              /^[0-9]*$/,
              $localize`:Invalid phone number error message: Please enter a valid phone number`,
            ),
            required(
              $localize`:Phone number required error message: Please enter a phone number`,
            ),
          ]);
        }
      }),
    );

    this.threatmetrix.load();
  }

  ngOnDestroy(): void {
    this.subscriptions.unsubscribe();
  }

  handleEmailClick(): void {
    this.setStep(SignupStep.DETAILS);
    this.showGoogleLogin = false;
    this.defaultHeading = $localize`Sign up with email`;
  }

  setStep(step: SignupStep): void {
    this.currentStep = step;
    this.changeDetectorRef.markForCheck();
  }

  async handleBackAction(): Promise<void> {
    if (this.currentStep === SignupStep.USERNAME_SELECT) {
      if (this.ssoAuthPromise !== undefined) {
        return this.setStep(SignupStep.RECOVERY_EMAIL);
      }
      return this.setStep(SignupStep.DETAILS);
    }

    this.currentStep--;

    if (this.currentStep === 0) {
      this.handleBackToStart();
    }
  }

  handleBackToStart(): void {
    this.currentStep = SignupStep.DETAILS;
  }

  handleSubmitDetails(type: 'sso' | 'normal' = 'normal'): void {
    this.checkDetailsPromise = this.loginSignup.checkUserDetails({
      email: this.detailsFormGroup.value.email,
    });
    this.checkDetailsPromise.then(response => {
      if (response?.status === 'success') {
        if (type === 'normal') {
          this.ssoAuthPromise = undefined;
          this.ssoDetailsPromise = undefined;
        }

        this.setStep(SignupStep.USERNAME_SELECT);
      }
    });
  }

  handleSubmitUsername(): void {
    // username select step handles the checkUserDetails
    // TODO: T267853 - move that validation logic into this OnInit for consistency?
    if (this.forceAccountType) {
      this.accountTypeFormGroup.patchValue({
        accountType: this.forceAccountType,
      });
      if (
        !this.authConfig.gdprCookie ||
        this.cookies.get(this.authConfig.gdprCookie) !== 'true' ||
        this.route.snapshot.queryParams.debug_skip_gdpr === 'true'
      ) {
        // enable emails for non EU users
        this.gdprFormGroup.patchValue({
          privacyConsent: true,
          personalUse: undefined,
        });
      }
      this.handleSignup();
    } else {
      this.setStep(SignupStep.ACCOUNT_TYPE);
    }
  }

  handleSubmitAccountType(): void {
    if (
      this.authConfig.gdprCookie &&
      this.cookies.get(this.authConfig.gdprCookie) === 'true' &&
      this.route.snapshot.queryParams.debug_skip_gdpr !== 'true'
    ) {
      // ask for privacy consent if user is European
      this.setStep(SignupStep.GDPR);
    } else {
      // assume privacy consent otherwise since user has no rights
      this.gdprFormGroup.patchValue({
        privacyConsent: true,
        personalUse: undefined,
      });
      this.handleSignup();
    }
  }

  handleSubmitGDPR(): void {
    this.handleSignup();
  }

  handleSignup(): void {
    if (this.ssoAuthPromise !== undefined) {
      this.signupPromise = this.handleSSOSignup();
      return;
    }

    this.signupPromise = this.loginSignup.signup({
      ...this.usernameFormGroup.getRawValue(),
      ...this.detailsFormGroup.getRawValue(),
      ...this.gdprFormGroup.getRawValue(),
      captcha: this.captchaFormGroup.getRawValue(),
      role: this.accountTypeFormGroup.controls.accountType.value,
      emailVerifyGoto: this.emailVerifyGoto,
    });

    this.signupPromise.then(response => {
      if (response?.status === 'success') {
        this.handleSuccess(
          response.result.user,
          response.result.token,
          'signUpForm',
        );
        if (
          this.accountTypeFormGroup.controls.accountType.value === 'employer' &&
          this.cookies.get('LANDED_ON_LOW_INTENT_SEM') === 'true'
        ) {
          this.cookies.remove('LANDED_ON_LOW_INTENT_SEM');
          this.cookies.put('SIGNED_UP_FROM_LOW_INTENT_SEM', 'true');
          this.tracking.trackCustomEvent(
            'CompleteRegistration',
            'Signup',
            {},
            { facebookEventName: FacebookStandardEvents.COMPLETE_REGISTRATION },
          );
        }
        return;
      }
      if (response?.errorCode === ErrorCodeApi.SIGNUP_CAPTCHA_REQUIRED) {
        this.captchaFormGroup.patchValue({
          challenge: response.requestId,
        });
        this.signupPromise = undefined;
        this.setStep(SignupStep.CAPTCHA);
      }
    });
  }

  async handleSSOSignup(): Promise<
    ResponseData<AuthSuccessResponseAjax, BackendAllErrorCodes | LoginError>
  > {
    const auth = await this.ssoAuthPromise;
    const userDetails = await this.ssoDetailsPromise;
    const initialRequest = await this.ssoLoginPromise;

    if (
      // if the initial auth request wasn't a success
      // shouldn't happen - this is just for type safety
      initialRequest?.status === 'error' ||
      !('action' in (initialRequest?.result ?? {})) ||
      // or if sso errored
      auth?.status === 'error' ||
      // or if we didn't get their details properly
      !userDetails
    ) {
      const errorCode =
        auth?.provider === 'facebook'
          ? ErrorCodeApi.AUTH_FACEBOOK_LINK_FAILED
          : auth?.provider === 'google'
            ? ErrorCodeApi.AUTH_GOOGLE_LINK_FAILED
            : ErrorCodeApi.AUTH_APPLE_LINK_FAILED;
      return {
        status: 'error',
        errorCode,
      };
    }

    const result: AuthResponse | EmptyObject = initialRequest?.result ?? {};
    const signupResponse = await this.loginSignup.signup({
      ...this.usernameFormGroup.getRawValue(),
      ...this.gdprFormGroup.getRawValue(),
      ssoDetails: auth?.result,
      ssoResponseToken: 'ssoToken' in result ? result.ssoToken : undefined,
      role: this.accountTypeFormGroup.controls.accountType.value,
      email: this.emailControl.value,
      ssoProvider: auth?.provider,
    });

    if (signupResponse.status === 'success') {
      this.handleSuccess(
        signupResponse.result.user,
        signupResponse.result.token,
        auth?.provider ?? 'SSOSignup',
      );
    }
    return signupResponse;
  }

  handleSuccess(userId: number, authToken: string, signUpMethod: string): void {
    if (!this.skipSuccessRedirectView) {
      this.setStep(SignupStep.SUCCESS);
    }

    this.tracking.trackCustomEvent('sign_up', 'Sign Up Form', {
      method: signUpMethod,
    });

    this.loginSignup
      .handleSuccess('signup', userId, authToken, false, {
        redirectDisabled: this.isModal && !this.shouldRedirect,
      })
      .then(() =>
        this.success.emit({
          userId,
          successStatus:
            this.accountTypeFormGroup.controls.accountType.value === 'employer'
              ? SuccessStatus.SIGNUP_EMPLOYER
              : SuccessStatus.SIGNUP_FREELANCER,
        }),
      );
  }

  /**
   * Displays "Login Success!" and uses login redirect URL.
   * Called from a successful sso login.
   */
  handleLoginSuccess(userId: number, authToken: string): void {
    if (!this.skipSuccessRedirectView) {
      this.setStep(SignupStep.LOGIN_SUCCESS);
    }
    this.loginSignup
      .handleSuccess('login', userId, authToken, false, {
        redirectDisabled: this.isModal && !this.shouldRedirect,
      })
      .then(() =>
        this.success.emit({
          userId,
          successStatus: SuccessStatus.LOGIN,
        }),
      );
  }

  handleSwitchToLogin(): void {
    this.loginSignup.saveState({
      user: this.detailsFormGroup.controls.email.value,
      password: this.detailsFormGroup.controls.password.value,
    });

    this.switchToLogin.emit();
  }

  handleSSOSwitchToLogin(): void {
    // save sso state and redirect to login to access link components
    this.loginSignup.saveState({
      ssoAuthPromise: this.ssoAuthPromise,
      ssoDetailsPromise: this.ssoDetailsPromise,
      ssoActionPromise: this.ssoLoginPromise,
    });

    this.switchToLogin.emit();
  }

  handleSSOSignupClick(provider: 'apple' | 'facebook' | 'google'): void {
    const { authPromise, detailsPromise, loginPromise } =
      this.loginSignup.triggerSSOLogin(provider);

    this.ssoLoginPromise = loginPromise.then(async response => {
      if (response?.status === 'error') {
        return response;
      }

      this.ssoAuthPromise = authPromise;
      this.ssoDetailsPromise = detailsPromise;
      if ('token' in response.result) {
        // this is a facebook login, so we use login success
        this.handleLoginSuccess(response.result.user, response.result.token);
      } else if (response.result.action === 'link') {
        this.handleSSOSwitchToLogin();
      } else if (response.result.action === 'signup') {
        // user needs to signup
        this.setStep(SignupStep.RECOVERY_EMAIL);
      }
      return response;
    });
  }
}
